import React from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant = 'primary' | 'purple' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'outline';
export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  pulse?: boolean;
  children: React.ReactNode;
}

const variantStyles: Record<BadgeVariant, { container: string; dot: string }> = {
  primary: {
    container: 'bg-accent text-accent-foreground border-primary/20',
    dot: 'bg-primary',
  },
  purple: {
    container: 'bg-plan-muted text-plan-on border-plan-border',
    dot: 'bg-plan',
  },
  success: {
    container: 'bg-success-muted text-success-on border-success-border',
    dot: 'bg-success',
  },
  warning: {
    container: 'bg-warning-muted text-warning-on border-warning-border',
    dot: 'bg-warning',
  },
  danger: {
    container: 'bg-destructive/10 text-destructive-on border-destructive/25',
    dot: 'bg-destructive',
  },
  info: {
    container: 'bg-info-muted text-info-on border-info-border',
    dot: 'bg-info',
  },
  neutral: {
    container: 'bg-secondary text-secondary-foreground border-border/80',
    dot: 'bg-muted-foreground',
  },
  outline: {
    container: 'bg-transparent text-foreground border-border/80 dark:border-border',
    dot: 'bg-primary',
  },
};

const sizeStyles: Record<BadgeSize, string> = {
  xs: 'text-[11px] px-2 py-0.5 font-medium leading-tight',
  sm: 'text-xs px-2.5 py-0.5 font-medium leading-tight',
  md: 'text-xs px-3 py-1 font-medium leading-normal',
  lg: 'text-[13px] px-3.5 py-1 font-medium leading-normal',
};

export const Badge: React.FC<BadgeProps> = ({
  variant,
  size = 'sm',
  dot = false,
  pulse = false,
  className,
  children,
  ...props
}) => {
  const styles = (variant && variantStyles[variant])
    ? variantStyles[variant]
    : className
      ? { container: '', dot: 'bg-primary' }
      : variantStyles.neutral;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border shadow-xs transition-colors',
        styles.container,
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {dot && (
        <span className="relative flex h-1.5 w-1.5">
          {pulse && (
            <span
              className={cn(
                'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                styles.dot
              )}
            />
          )}
          <span className={cn('relative inline-flex rounded-full h-1.5 w-1.5', styles.dot)} />
        </span>
      )}
      {children}
    </span>
  );
};
